/**
 * Máscara react-input-mask para CNPJ (legado e alfanumérico).
 * O wildcard `*` aceita letra ou dígito; `9` aceita apenas dígito.
 * Grupos: 2 - 3 - 3 / 4 - 2 (mesmo formato visual do CNPJ numérico).
 */
export const CNPJ_MASK = '**.***.***\\/****-99';

/**
 * formatChars para react-input-mask compatível com CNPJ numérico e alfanumérico.
 * Pode ser passado em todos os inputs CPF/CNPJ — `9` já é o padrão numérico.
 */
export const CNPJ_FORMAT_CHARS: Record<string, string> = {
    '9': '[0-9]',
    '*': '[A-Za-z0-9]',
};

/**
 * Utilitários para CNPJ — suporta formato numérico legado e alfanumérico (RF ~07/2026).
 *
 * Formato legado:  14 dígitos, ex.: 89.306.448/0001-41
 * Formato novo:    12 posições [A-Z0-9] + 2 dígitos verificadores, ex.: A1.B2C.3D4/E5A9-41
 *
 * O cálculo dos DVs usa módulo 11 com valor do caractere = ASCII(upper) − 48,
 * o que para dígitos resulta no próprio número — mantendo 100% de compatibilidade
 * com o algoritmo anterior.
 */

/**
 * Remove apenas a pontuação de máscara (`.`, `/`, `-`), preservando letras e dígitos.
 * Use esta função sempre que precisar do CNPJ "limpo" para enviar à API ou comparar.
 *
 * Diferente de `replace(/\D/g, '')`, este utilitário NÃO remove letras.
 */
export function normalizeCnpj(value: string): string {
  return (value ?? '').replace(/[.\-/]/g, '').toUpperCase().trim();
}

/**
 * Retorna `true` se o valor normalizado (sem máscara) tem comprimento de CNPJ (14 chars).
 * Use junto com `normalizeCnpj`:
 *
 * ```ts
 * const clean = normalizeCnpj(doc);
 * const isCnpj = isCnpjLength(clean); // true para CNPJ, false para CPF (11)
 * ```
 */
export function isCnpjLength(normalizedValue: string): boolean {
  return normalizedValue.length === 14;
}

/** Valor do caractere para módulo 11 (RF: ASCII decimal − 48). */
function charValue(char: string): number {
  return char.toUpperCase().charCodeAt(0) - 48;
}

const CNPJ_ALL_SAME_NUMERIC = [
  '00000000000000', '11111111111111', '22222222222222', '33333333333333',
  '44444444444444', '55555555555555', '66666666666666', '77777777777777',
  '88888888888888', '99999999999999',
];

/**
 * Valida CNPJ — aceita formato numérico (legado) e alfanumérico (novo, RF ~07/2026).
 *
 * Pode receber o valor com ou sem máscara; a normalização é feita internamente.
 */
export function validateCNPJ(value: string | null | undefined): boolean {
  const cnpj = normalizeCnpj(value ?? '');

  if (cnpj.length !== 14) return false;

  // Estrutura: 12 posições alfanuméricas + 2 DVs obrigatoriamente numéricos
  if (!/^[A-Z0-9]{12}\d{2}$/.test(cnpj)) return false;

  // Rejeita sequências repetidas (apenas para CNPJ 100% numérico, como na regra original)
  if (/^\d{14}$/.test(cnpj) && CNPJ_ALL_SAME_NUMERIC.includes(cnpj)) return false;

  // ── DV 1 ──────────────────────────────────────────────────────────────────
  let size = 12;
  let sum = 0;
  let pos = size - 7;
  for (let i = size; i >= 1; i--) {
    sum += charValue(cnpj.charAt(size - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  const dv1 = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (dv1 !== parseInt(cnpj.charAt(12), 10)) return false;

  // ── DV 2 ──────────────────────────────────────────────────────────────────
  size = 13;
  sum = 0;
  pos = size - 7;
  for (let i = size; i >= 1; i--) {
    sum += charValue(cnpj.charAt(size - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  const dv2 = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  return dv2 === parseInt(cnpj.charAt(13), 10);
}
